import React, { useState } from 'react';
import { colorSet } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { Box, makeStyles } from '@material-ui/core';
import {
  CapaQuotationChatMessageType,
  FetchQuotationChatMessagesDocument,
  useChangeQuotationMessageInvisibleMutation,
  useRegisterRequestRfqQuotationMutation
} from '@generated/graphql';
import { useSelector } from '@store';
import ChatQuitModal from '@components/Chat/QuitModal';
import { isClosedTypes } from '@components/Chat/types';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      width: 230,
      borderRadius: 10,
      backgroundColor: colorSet.gray000
    },
    header: {
      borderRadius: '10px 10px 0px 0px',
      padding: '10px 16px',
      color: colorSet.gray000,
      backgroundColor: colorSet.primary500
    },
    contentWrapper: {
      padding: '0px 16px 16px 16px'
    },
    content: {
      paddingTop: 16,
      borderTop: `1px solid ${colorSet.gray200}`
    },
    wordBreak: {
      wordBreak: 'break-all',
      '& b': {
        wordBreak: 'break-all'
      }
    },
    cta: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: 16,
      '& .mb-10': {
        marginBottom: 10
      },
      '& .mb-24': {
        marginBottom: 24
      },
      '& .text': {
        ...theme.typography.body2,
        color: colorSet.gray700,
        textDecoration: 'underline'
      }
    }
  };
});

interface Props {
  senderId: string | undefined;
}

const InduceQuotationRequestBubble = ({ senderId }: Props) => {
  const classes = useStyles();
  const rfqId = useSelector((state) => state.chat.rfqId);
  const partnerId = useSelector((state) => state.chat.quotationPartnerId);

  const chatRoomId = useSelector((state) => state.chat.chatRoomId);

  const chatRoomStatus = useSelector((state) => state.chat.chatRoomStatus);
  const hasQuotation = useSelector((state) => state.chat.hasQuotation ?? false);

  const isClosed = chatRoomStatus
    ? isClosedTypes.includes(chatRoomStatus)
    : false;

  const [modal, setModal] = useState<'fix' | 'close' | null>(null);

  const [requestTrigger] = useRegisterRequestRfqQuotationMutation({
    variables: {
      input: {
        rfqId: rfqId ?? '',
        partnerId: partnerId ?? ''
      }
    }
  });

  const [invisibleTrigger] = useChangeQuotationMessageInvisibleMutation();

  const request = () => {
    if (!rfqId || !partnerId) return;

    requestTrigger();
  };

  const invisible = () => {
    if (!chatRoomId || !partnerId) return;

    invisibleTrigger({
      variables: {
        input: {
          chatRoomId: chatRoomId,
          senderId: senderId,
          messageType: CapaQuotationChatMessageType.InduceQuotationRequest
        }
      },
      refetchQueries: [
        {
          query: FetchQuotationChatMessagesDocument,
          variables: {
            input: {
              chatRoomId
            }
          },
          fetchPolicy: 'network-only'
        }
      ],
      awaitRefetchQueries: true
    });
  };

  const handleModal = (open: 'fix' | 'close' | null) => {
    setModal(open);
  };

  return (
    <>
      {modal === 'close' && (
        <ChatQuitModal open={true} onClose={() => handleModal(null)} />
      )}

      <Box className={classes.root}>
        <div className={classes.header}>
          <CustomizedTypography
            variant="body2"
            fontWeight="bold"
            color="inherit"
          >
            견적서
          </CustomizedTypography>
        </div>
        <div className={classes.contentWrapper}>
          <div className={classes.content}>
            <CustomizedTypography
              variant="body1"
              fontWeight="bold"
              gutterBottoms={10}
            >
              견적서 요청하기
            </CustomizedTypography>
            <CustomizedTypography variant="body2" className={classes.wordBreak}>
              상담을 진행하셨다면 견적서를 요청하세요.
            </CustomizedTypography>
          </div>
        </div>

        <div className={classes.cta}>
          <CustomizedButton
            fullWidth
            variant="contained"
            color="primary"
            className="mb-10"
            onClick={request}
            disabled={isClosed || hasQuotation}
          >
            견적서 요청하기
          </CustomizedButton>
          <CustomizedButton
            fullWidth
            variant="contained"
            color="default"
            className="mb-24"
            disabled={isClosed || hasQuotation}
            onClick={invisible}
          >
            아직 상담중에요
          </CustomizedButton>
          <CustomizedButton
            fullWidth
            variant="text"
            className="text"
            color="default"
            onClick={() => handleModal('close')}
            disabled={isClosed || hasQuotation}
          >
            추가 상담을 진행하지 않을래요
          </CustomizedButton>
        </div>
      </Box>
    </>
  );
};

export default InduceQuotationRequestBubble;
